import {
  getFreshBearerToken,
  isImpersonationActive,
  recoverAuthAfter401,
} from "@healthos/auth/react";
import type { WithSignalInit } from "@healthos/client-data";

/**
 * The method a call will actually go out as: `init.method` wins, falling
 * back to a `Request` input's own method (RequestInfo | URL admits a
 * `Request`), and finally the fetch default of GET.
 */
function resolveMethod(input: RequestInfo | URL, init?: Pick<RequestInit, "method">): string {
  const method = init?.method ?? (input instanceof Request ? input.method : undefined);
  return (method ?? "GET").toUpperCase();
}

// Starts the ceiling here, not wherever `init` was built: AbortSignal.timeout()
// counts from creation, and by the time authedFetch reaches the real fetch it
// may already have spent seconds/tens-of-seconds awaiting getFreshBearerToken
// (OIDC silent renew, the desktop host-auth handshake). A timeout composed
// earlier would eat that budget and abort an otherwise-healthy request.
function withTimeout(signal: AbortSignal | null | undefined, timeoutMs: number | undefined) {
  if (timeoutMs === undefined) return signal ?? undefined;
  const timeout = AbortSignal.timeout(timeoutMs);
  return signal ? AbortSignal.any([signal, timeout]) : timeout;
}

/**
 * Browser-side fetch wrapper that attaches the current access token as a Bearer
 * on every API call, renewing first if it is near expiry so a token that lapses
 * mid-session does not 401 the next write.
 *
 * On a 401, recovery depends on the session source. In OIDC mode there is
 * nothing to retry with, so the stale user is removed (flipping the app to
 * signed-out) and the 401 surfaces. In host-auth mode the desktop host is asked
 * for a fresh token and the request is retried once; only if the host can't
 * refresh does the 401 surface.
 *
 * `init.timeoutMs` (set by @healthos/client-data's withSignal(), for
 * Plan/Snapshot/Community/Library queries) applies a hang-protection ceiling
 * starting fresh right before each real fetch. Callers that pass their own
 * signal directly and no timeoutMs (e.g. brief-audio.ts's 90s download) are
 * untouched - they own their own bound.
 *
 * Read-only guard: a support "Log in as user" impersonation session (see
 * `../lib/impersonation-auth.ts`) reuses this same fetch path, so every
 * write goes through here too. While impersonating, any non-GET request is
 * refused before it is sent (and before a token is even requested) rather
 * than let a support session mutate a real member's data.
 */
export async function authedFetch(
  input: RequestInfo | URL,
  init?: WithSignalInit,
): Promise<Response> {
  const { timeoutMs, signal, ...rest } = init ?? {};
  const method = resolveMethod(input, rest);
  // Impersonation sessions are read-only. Refuse any mutating method
  // before a token is even requested. HEAD is allowed (the server permits it).
  // Return a synthetic 403 Response rather than throwing, so callers see the
  // same shape as a server refusal and flow through the app's normal ApiError
  // handling instead of a rejected promise with an opaque message.
  if (isImpersonationActive() && method !== "GET" && method !== "HEAD") {
    return new Response(JSON.stringify({ error: "impersonation sessions are read-only" }), {
      status: 403,
      headers: { "Content-Type": "application/json" },
    });
  }
  const token = await getFreshBearerToken();
  const headers = new Headers(rest.headers);
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  const res = await fetch(input, { ...rest, headers, signal: withTimeout(signal, timeoutMs) });
  if (res.status !== 401) return res;

  const refreshed = await recoverAuthAfter401();
  if (!refreshed) return res;

  const retryHeaders = new Headers(rest.headers);
  retryHeaders.set("Authorization", `Bearer ${refreshed}`);
  return fetch(input, { ...rest, headers: retryHeaders, signal: withTimeout(signal, timeoutMs) });
}
